import { useNavigate, useSearchParams } from "@solidjs/router"
import {
  createMemo,
  createResource,
  createSignal,
  Match,
  Show,
  Switch,
} from "solid-js"
import z from "zod"
import { Text } from "@/designSystem/components/text"
import {
  bodyClass,
  buttonClass,
  descriptionClass,
  footerClass,
  formClass,
  formContainerClass,
  inputClass,
  linkClass,
  mainClass,
  titleClass,
  welcomeImageClass,
} from "./subscribe.css"

const BACKEND_URL = import.meta.env.VITE_FIKA_BACKEND_URL
const PUBLIC_URL = import.meta.env.VITE_FIKA_PUBLIC_URL
const searchParamsSchema = z.object({
  userId: z.string(),
  returnTo: z.string(),
})
type SearchParams = z.infer<typeof searchParamsSchema>

// TODO: share contract with API
type User = {
  id: string
  title: string
  name: string
  description: string
  welcome_image_url: string
}

export function Subscribe() {
  const [searchParams] = useSearchParams()
  const parsedSearchParams = createMemo(() =>
    searchParamsSchema.safeParse(searchParams),
  )

  return (
    <Show
      when={parsedSearchParams().data}
      fallback={
        <ErrorMessage
          message={`error parsing search params ${parsedSearchParams().error?.message}`}
        />
      }
    >
      {params => <SubscribeWithSearchParams {...params()} />}
    </Show>
  )
}

function SubscribeWithSearchParams(props: SearchParams) {
  const [user] = createResource(props.userId, userId =>
    fetchUser(BACKEND_URL, userId),
  )

  return (
    <Switch>
      <Match when={user.loading}>
        <Loading />
      </Match>
      <Match when={user.error}>
        <ErrorMessage message={user.error} />
      </Match>
      <Match when={user()}>
        {user => <SubscribeWithUser user={user()} params={props} />}
      </Match>
    </Switch>
  )
}

function SubscribeWithUser(props: { user: User; params: SearchParams }) {
  const [errors, setErrors] = createSignal<string | undefined>()
  const navigate = useNavigate()

  async function onSubmit(e: SubmitEvent) {
    e.preventDefault()
    const email = (e.target as HTMLFormElement).email.value

    if (email.length < 1) {
      setErrors("Please enter an email address")
    } else {
      const response = await fetch(`${BACKEND_URL}/api/emails/${email}`, {
        headers: { "Content-Type": "application/json" },
      })
      const mode = response.ok ? "login" : "subscribe"

      navigate(
        `/subscribe/${email}?${new URLSearchParams({ ...props.params, mode }).toString()}`,
      )
    }
  }

  return (
    <div class={bodyClass}>
      {props.user.welcome_image_url ? (
        <img
          src={props.user.welcome_image_url}
          alt={props.user.title ?? props.user.name}
          class={welcomeImageClass}
        />
      ) : null}
      <main class={mainClass}>
        <h1 class={titleClass}>
          Subscribe to "{props.user.title ?? props.user.name}"
        </h1>
        <div class={formContainerClass}>
          <h2 class={descriptionClass}>what's your email?</h2>
          <form class={formClass} onSubmit={onSubmit}>
            <input
              class={inputClass}
              name="email"
              type="email"
              placeholder="Type your email…"
            />
            <button class={buttonClass} type="submit">
              subscribe
            </button>
          </form>
        </div>
        <Show when={errors()}>{errors => <p>{errors()}</p>}</Show>
      </main>
      <footer class={footerClass}>
        By submitting, you are accepting the{" "}
        <a
          class={linkClass}
          href={`${PUBLIC_URL}/privacy-policy`}
          target="_blank"
          rel="noopener noreferrer"
        >
          privacy policy
        </a>
        , and agreeing the{" "}
        <a
          class={linkClass}
          href={`${PUBLIC_URL}/terms-of-use`}
          target="_blank"
          rel="noopener noreferrer"
        >
          terms of service
        </a>
        .
      </footer>
    </div>
  )
}

async function fetchUser(backendUrl: string, userId: string) {
  const response = await fetch(`${backendUrl}/api/users/${userId}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json" },
  })

  return (await response.json()) as User
}

function Loading() {
  return (
    <div class={bodyClass}>
      <main class={mainClass}>
        <span>Loading...</span>
      </main>
    </div>
  )
}

function ErrorMessage(props: { message: string }) {
  return (
    <div class={bodyClass}>
      <main class={mainClass}>
        <Text color="rose600">Error: {props.message}</Text>
      </main>
    </div>
  )
}
